
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {AccountMarginSnapshot} from '../entity/AccountMarginSnapshot';
import {FieldModelAccountMarginSnapshot} from '../entity/AccountMarginSnapshot';

/**
 * DailySnapshotResult struct
 */
class DailySnapshotResult {
  /**
   * Initialize struct
   * @param {!AccountMarginSnapshot=} dailyMarginSnapshot
   * @constructor
   */
  constructor (dailyMarginSnapshot = new AccountMarginSnapshot()) {
    this.DailyMarginSnapshot = dailyMarginSnapshot
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DailySnapshotResult}
   * @param {!DailySnapshotResult} other Other struct
   * @returns {!DailySnapshotResult} This struct
   */
  copy (other) {
    if (other.DailyMarginSnapshot != null) {
      this.DailyMarginSnapshot = AccountMarginSnapshot.fromObject(other.DailyMarginSnapshot)
    } else {
      this.DailyMarginSnapshot = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DailySnapshotResult}
   * @returns {!DailySnapshotResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DailySnapshotResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DailySnapshotResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DailySnapshotResult}
   * @param {!DailySnapshotResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DailySnapshotResult)) {
      throw new TypeError('Instance of DailySnapshotResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DailySnapshotResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DailyMarginSnapshot: ((this.DailyMarginSnapshot != null) ? this.DailyMarginSnapshot : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DailySnapshotResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DailySnapshotResult} other Object value
   * @returns {!DailySnapshotResult} Created struct
   */
  static fromObject (other) {
    return new DailySnapshotResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DailySnapshotResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DailySnapshotResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DailySnapshotResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10047
  }
}

export { DailySnapshotResult };

/**
 * Fast Binary Encoding DailySnapshotResult field model
 */
class FieldModelDailySnapshotResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DailyMarginSnapshot = new FieldModelAccountMarginSnapshot(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DailyMarginSnapshot.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DailyMarginSnapshot.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDailySnapshotResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10047
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDailySnapshotResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDailySnapshotResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DailyMarginSnapshot.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DailyMarginSnapshot.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DailyMarginSnapshot.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDailySnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDailySnapshotResult}
   * @param {!DailySnapshotResult} fbeValue Default value, defaults is new DailySnapshotResult()
   * @returns {!DailySnapshotResult} DailySnapshotResult value
   */
  get (fbeValue = new DailySnapshotResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDailySnapshotResult}
   * @param {!DailySnapshotResult} fbeValue DailySnapshotResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DailyMarginSnapshot.fbeSize) <= fbeStructSize) {
      fbeValue.DailyMarginSnapshot = this.DailyMarginSnapshot.get()
    } else {
      fbeValue.DailyMarginSnapshot = new AccountMarginSnapshot()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DailyMarginSnapshot.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDailySnapshotResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDailySnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDailySnapshotResult}
   * @param {!DailySnapshotResult} fbeValue DailySnapshotResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDailySnapshotResult}
   * @param {DailySnapshotResult} fbeValue DailySnapshotResult value
   */
  setFields (fbeValue) {
    this.DailyMarginSnapshot.set(fbeValue.DailyMarginSnapshot)
  }
}

export { FieldModelDailySnapshotResult };

/**
 * Fast Binary Encoding DailySnapshotResult model
 */
class DailySnapshotResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDailySnapshotResult(this.buffer, 4)
  }

  /**
   * Get the DailySnapshotResult model
   * @this {!DailySnapshotResultModel}
   * @returns {!FieldModelDailySnapshotResult} model DailySnapshotResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DailySnapshotResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DailySnapshotResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DailySnapshotResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DailySnapshotResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDailySnapshotResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DailySnapshotResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DailySnapshotResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DailySnapshotResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DailySnapshotResultModel}
   * @param {!DailySnapshotResult} value DailySnapshotResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DailySnapshotResultModel}
   * @param {!DailySnapshotResult} value DailySnapshotResult value, defaults is new DailySnapshotResult()
   * @return {!object} Deserialized DailySnapshotResult value and its size
   */
  deserialize (value = new DailySnapshotResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DailySnapshotResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DailySnapshotResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DailySnapshotResultModel}
   * @param {!number} prev Previous DailySnapshotResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DailySnapshotResultModel };
